import { ConfirmDialog } from '@/components/confirm-dialog';
import { companiesApi } from '@/lib/api';
import type { ArchivedCompany } from '@mss_creator/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export const DELETE_COMPANY_ERROR_MESSAGE = 'La suppression a échoué. Réessayez.';

/**
 * What a permanent deletion takes, worded for the number of matrices the
 * company has (issue #20, planche 04): « Ses N matrices », « Sa matrice », or
 * nothing to count.
 */
export function deleteCompanyDescription(matrixCount: number): string {
  const rest =
    'setup de singularité, ses paliers et tout leur historique seront supprimés. Cette action est irréversible.';
  if (matrixCount === 0) return `Son ${rest}`;
  if (matrixCount === 1) return `Sa matrice, son ${rest}`;
  return `Ses ${matrixCount} matrices, son ${rest}`;
}

export interface DeleteCompanyDialogProps {
  /** The archived company to delete for good; `null` keeps the dialog closed. */
  company: Pick<ArchivedCompany, 'id' | 'name' | 'matrixCount'> | null;
  onClose: () => void;
}

/**
 * Confirmation before permanently deleting an archived company (issue #20,
 * planche 04). The focus lands on « Annuler »; « Supprimer » is the
 * destructive button; on failure the dialog stays open with the error and
 * « Supprimer » retries.
 */
export function DeleteCompanyDialog({ company, onClose }: DeleteCompanyDialogProps) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (id: string) => companiesApi.delete(id),
    onSuccess: (_result, id) => {
      queryClient.removeQueries({ queryKey: ['company', id] });
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
      onClose();
    },
  });

  function handleCancel() {
    mutation.reset();
    onClose();
  }

  return (
    <ConfirmDialog
      open={company !== null}
      title={company ? `Supprimer définitivement « ${company.name} » ?` : ''}
      description={company ? deleteCompanyDescription(company.matrixCount) : ''}
      confirmLabel="Supprimer"
      pendingLabel="Suppression…"
      confirmIcon="delete"
      confirmVariant="destructive"
      isPending={mutation.isPending}
      errorMessage={mutation.isError ? DELETE_COMPANY_ERROR_MESSAGE : null}
      onConfirm={() => {
        if (company) mutation.mutate(company.id);
      }}
      onCancel={handleCancel}
    />
  );
}
